import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {EnrollmentType} from "../../../../../domain/enums/enrollment-type.enum";
import {TaskStatusEnum} from "../../../../../domain/enums/task-status.enum";
import {
  DefaultEnrollmentComponent
} from "../../../../../common/enrollment-request/default-enrollment/default-enrollment.component";
import {
  IndividualBedEnrollmentComponent
} from "../../../../../common/enrollment-request/individual-bed-enrollment/individual-bed-enrollment.component";
import {
  IndividualOwnerEnrollmentComponent
} from "../../../../../common/enrollment-request/individual-owner-enrollment/individual-owner-enrollment.component";
import {MatButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {CommonModule} from "@angular/common";
import {
  PropertyManagementEnrollmentComponent
} from "../../../../../common/enrollment-request/property-management-enrollment/property-management-enrollment.component";
import {EnrollmentComponent} from "../../../../../common/enrollment-request/EnrollmentComponent";
import {buildValidationErrorMessage} from "../../../../../utils/invalid-fields.util";
import {ApproveDialogComponent} from "../../../../../common/enrollment-request/approve-dialog/approve-dialog.component";
import {DeclineDialogComponent} from "../../../../../common/enrollment-request/decline-dialog/decline-dialog.component";
import {TaskModel} from "../../../../../domain/models/task.model";
import {EnrollmentModel} from "../../../../../domain/models/enrollment.model";
import {TaskService} from "../../../../../domain/services/task/task.service";
import {EnrollmentService} from "../../../../../domain/services/enrollment/enrollment.service";
import {Router} from "@angular/router";
import {DialogService} from "../../../../../domain/services/dialog/dialog.service";
import {EnrollmentStatus} from "../../../../../domain/enums/enrollment-status.enum";
import {TaskActionBarComponent} from "../task-action-bar/task-action-bar.component";
import {UnderConstructionComponent} from "../../../../../common/under-construction/under-construction.component";
import {PropertyMatchCardComponent} from "../../../../../common/property-match-card/property-match-card.component";
import {PropertyShareService} from "../../../../../domain/services/property-share/property-share.service";
import {PropertyMatch} from "../../../../../domain/models/property-share.model";

@Component({
  selector: 'app-new-customer-task',
  standalone: true,
  imports: [DefaultEnrollmentComponent, IndividualBedEnrollmentComponent, IndividualOwnerEnrollmentComponent,
    MatButton, MatIcon, CommonModule, PropertyManagementEnrollmentComponent, TaskActionBarComponent, UnderConstructionComponent, PropertyMatchCardComponent],
  templateUrl: './new-customer-task.component.html',
  styleUrl: './new-customer-task.component.css'
})
export class NewCustomerTaskComponent implements OnInit {
  @Input() currentTask!: TaskModel;
  @ViewChild(DefaultEnrollmentComponent) defaultEnrollmentComponent!: DefaultEnrollmentComponent;
  @ViewChild(IndividualBedEnrollmentComponent) individualBedEnrollmentComponent!: IndividualBedEnrollmentComponent;
  @ViewChild(IndividualOwnerEnrollmentComponent) individualOwnerEnrollmentComponent!: IndividualOwnerEnrollmentComponent;
  @ViewChild(PropertyManagementEnrollmentComponent) propertyManagementEnrollmentComponent!: PropertyManagementEnrollmentComponent;
  @ViewChild(TaskActionBarComponent) taskActionBar!: TaskActionBarComponent;

  currentEnrollment!: EnrollmentModel;
  recentlyLocked = false;
  submitEnabled = false;
  disableSubmit = false;
  protected readonly EnrollmentType = EnrollmentType;
  protected readonly TaskStatusEnum = TaskStatusEnum;

  /** Same-address properties on other accounts; Approve waits until each is Linked or dismissed. */
  unresolvedMatches: PropertyMatch[] = [];
  /** At most one link is sent with the approval (the backend links a single existing property). */
  pendingLink: { existingPropertyId: number; newIsHome: boolean } | null = null;
  linkedMatch: PropertyMatch | null = null;
  /** Approve stays disabled until the duplicate check has answered. */
  matchesLoading = true;

  constructor(private readonly taskService: TaskService,
              private readonly enrollmentService: EnrollmentService,
              private readonly propertyShareService: PropertyShareService,
              private readonly dialogService: DialogService,
              private readonly router: Router) {
  }

  ngOnInit(): void {
    this.fetchEnrollmentRequest(this.currentTask.enrollmentRequestId);
  }

  getEnrollmentComponent(): EnrollmentComponent | undefined {
    if (this.currentEnrollment?.enrollmentType) {
      switch (this.currentEnrollment?.enrollmentType) {
        case EnrollmentType.INDIVIDUAL_OWNER:
          return this.individualOwnerEnrollmentComponent;
        case EnrollmentType.PROPERTY_MANAGEMENT:
          return this.propertyManagementEnrollmentComponent;
        case EnrollmentType.INDIVIDUAL_BED:
          return this.individualBedEnrollmentComponent;
        default:
          return this.defaultEnrollmentComponent;
      }
    }
    return undefined;
  }

  onDiscartAndExit() {
    if (this.recentlyLocked) {
      this.currentTask.status = TaskStatusEnum.OPEN;
      this.taskService.update(this.currentTask.id, this.currentTask).subscribe(value => {
        this.currentTask = value;
        this.recentlyLocked = false;
        this.exit();
      });
    } else {
      this.exit();
    }
  }

  approve() {
    this.dialogService.openCustomDialog(ApproveDialogComponent, (value: boolean) => {
      if (value) {
        this.submitApprove();
      } else {
        this.taskActionBar.resetLoading();
      }
    });
  }

  decline() {
    this.dialogService.openCustomDialog(DeclineDialogComponent, (value: boolean) => {
      if (value) {
        this.submitDecline();
      } else {
        this.taskActionBar.resetLoading();
      }
    });
  }

  private fetchEnrollmentRequest(id: number) {
    this.enrollmentService.getEnrollmentById(id)
      .subscribe(value => {
        this.currentEnrollment = value;
        this.fetchMatches(id);
      });
  }

  private fetchMatches(enrollmentId: number) {
    this.matchesLoading = true;
    this.propertyShareService.enrollmentMatches(enrollmentId).subscribe({
      next: matches => {
        this.matchesLoading = false;
        this.unresolvedMatches = matches ?? [];
      },
      error: err => {
        this.matchesLoading = false;
        this.unresolvedMatches = [];
        // 404 = no rental property on the enrollment, nothing to match. Anything else: warn, don't block approval.
        if (err?.status !== 404) {
          this.dialogService.openErrorDialog('Duplicate check unavailable',
            'Could not check for duplicate properties. You can still approve, then use "Check for duplicates" on the PROPERTIES tab.');
        }
      },
    });
  }

  get newIsManagementCompany(): boolean {
    return this.currentEnrollment?.enrollmentType === EnrollmentType.PROPERTY_MANAGEMENT;
  }

  /** Link is applied on approval; the other candidates are then the same address on other accounts — drop them. */
  onLink(match: PropertyMatch, newIsHome: boolean) {
    this.pendingLink = { existingPropertyId: match.propertyId, newIsHome };
    this.linkedMatch = match;
    this.unresolvedMatches = [];
  }

  /** The new property has no id until approval, so "not the same" is only remembered for this approval. */
  onNotSame(match: PropertyMatch) {
    this.unresolvedMatches = this.unresolvedMatches.filter(m => m !== match);
  }

  clearLink() {
    this.pendingLink = null;
    this.linkedMatch = null;
    this.fetchMatches(this.currentTask.enrollmentRequestId);
  }

  private exit() {
    this.router.navigate(["/app/tasks"]);
  }

  private submitApprove() {
    const enrollmentComponent = this.getEnrollmentComponent();
    if (enrollmentComponent?.isFormValid()) {
      this.disableSubmit = true;
      const updated = {...enrollmentComponent?.getUpdatedEnrollment(), status: EnrollmentStatus.Approved};
      this.enrollmentService
        .approveEnrollment(updated, this.pendingLink).subscribe((res) => {
        this.recentlyLocked = false;
        this.currentTask.status = TaskStatusEnum.APPROVED;
        const warnings = res?.linkWarnings ?? [];
        if (!warnings.length) { this.exit(); return; }
        // Approval stands; the share did not. Tell staff before leaving the task (it is gone afterwards).
        this.dialogService.openErrorDialog('Approved, but not linked',
          `Approved, but the property was NOT linked: ${warnings.join('; ')}. `
            + `Link it from the account's PROPERTIES tab.`)
          .subscribe(() => this.exit());
      }, (error) => {
        this.dialogService.openErrorDialog("Unable to submit", error?.error?.message ?? error?.error?.error);
        this.disableSubmit = false;
        this.taskActionBar.resetLoading();
      });
    } else {
      this.taskActionBar.resetLoading();
      enrollmentComponent?.raiseFormValidations();
      // Wait for DOM to reflect invalid state, then collect field names
      setTimeout(() => {
        const msg = buildValidationErrorMessage();
        this.dialogService.openErrorDialog("Unable to submit", msg);
      });
    }
  }

  saving = false;

  /** Persist review edits (e.g. jurisdiction, office, owners) without approving. */
  saveEdits(): void {
    const enrollmentComponent = this.getEnrollmentComponent();
    if (!enrollmentComponent) return;
    this.saving = true;
    this.enrollmentService.saveForReview(enrollmentComponent.getUpdatedEnrollment()).subscribe({
      next: () => {
        this.saving = false;
        this.dialogService.openSuccesDialog('Saved', 'Your changes were saved. The task is still open.');
      },
      error: (error) => {
        this.saving = false;
        this.dialogService.openErrorDialog('Unable to save', error?.error?.message ?? error?.error?.error ?? 'Please try again.');
      },
    });
  }

  private submitDecline() {
    this.taskService.decline(this.currentTask).subscribe(() => {
      this.recentlyLocked = false;
      const updated = {...this.currentEnrollment, status: EnrollmentStatus.Declined};
      this.enrollmentService
        .updateEnrollment(updated).subscribe(() => {
        this.recentlyLocked = false;
        this.exit();
      });
    });
  }

  isTaskApproved($event: boolean) {
    if($event) {
      this.approve();
    } else {
      this.decline();
    }
  }
}
